// Return Status's overdue refund in the panel (2026-10-29, Jenn;
// @cxhub/shared's return-refund-overdue.js, server/src/ai/return-refund-overdue.js).
//
// The overdue path's steps show only when the stage is "Delivered to us" and
// its date is more than the window before today (SuggestionPanel.jsx's
// taskPathKey). Here:
//   - the values the email and the note fill as the CSR enters them (the
//     delivered date and the business days since, the refund step's amount)
//     and as the refund step's result comes back (the RTN number, eBay's
//     refund id);
//   - the refund step card's why line and amount breakdown;
//   - the step states a reload restores from the suggestion's CXHub notes
//     (the refund's result, the email sent);
//   - the email and the note wait on the refund step.
import { returnRefundOverdueState, overdueRefundWhy, fillOverdueRefundTokens, overdueToday } from '@cxhub/shared'

export const OVERDUE_EMAIL_STEP = 'email_support_zero_out_rma'
export const OVERDUE_TOKENS_HINT = 'Bracketed values fill in from the refund when it is processed.'

export function isOverdueRefundStep(a) {
  return Boolean(a?.params?.overdueRefund)
}
export const OVERDUE_NOTE_STEP = 'overdue_refund_add_note'
export function isOverdueNoteStep(a) {
  return a?.type === OVERDUE_NOTE_STEP
}
// The email and the note wait on the refund step.
export function waitsOnOverdueRefund(a) {
  return a?.type === OVERDUE_EMAIL_STEP || isOverdueNoteStep(a)
}
// The reply's template tag after the overdue refund (2026-10-29, the reply
// after Process): the Refund Confirmation the stage put in the box, which the
// result names (result.replyTemplate with no reply text, so the house
// template switch never replaces it with the scrap path's "Refund No
// Return"). null for any other step.
export function overdueReplyTag(action, result) {
  if (!isOverdueRefundStep(action)) return null
  const t = result?.replyTemplate
  return t && typeof t.title === 'string' && t.title ? { folder: t.folder ?? null, title: t.title } : null
}

export function overdueRefundStepIndex(actions) {
  return (actions ?? []).findIndex(isOverdueRefundStep)
}

// The refund step's amount: the CSR's, else the step's.
export function overdueAmountOf(step, input) {
  const raw = input?.refundAmount
  if (raw !== undefined && raw !== null && raw !== '') return raw
  return step?.params?.overdueRefund?.amount ?? null
}

// What fills the email and the note now. choice/date: the stage choice in
// the panel; stepStates: the panel's per-step states (the refund's result).
export function overdueLiveValues({ actions, choice, date, inputs = {}, stepStates = {}, today = overdueToday() } = {}) {
  const idx = overdueRefundStepIndex(actions)
  const step = idx >= 0 ? actions[idx] : null
  const window = step?.params?.overdueRefund?.windowBusinessDays ?? undefined
  const deliveredDate = choice === 'delivered' && date ? date : null
  const state = deliveredDate ? returnRefundOverdueState({ deliveredDate, today, windowBusinessDays: window }) : null
  const result = idx >= 0 ? stepStates?.[idx]?.result ?? null : null
  const amount = result?.overdueAmount ?? (step ? overdueAmountOf(step, inputs?.[idx]) : null)
  return {
    deliveredDate,
    businessDays: state?.businessDays ?? null,
    amount: amount === null || amount === '' || !Number.isFinite(Number(amount)) ? null : Number(amount),
    refundDate: today,
    rtnNumber: result?.erpReturnId ?? result?.rtnNumber ?? null,
    ebayRefundId: result?.ebayRefundId ?? null,
  }
}

// The email body the box shows: the CSR's edit, else the step's with every
// value known now filled in.
export function overdueEmailBody(step, input, live) {
  if (typeof input?.body === 'string') return input.body
  return fillOverdueRefundTokens(step?.params?.body ?? '', live ?? {})
}

// The refund step card's why line.
export function overdueWhyLine(step, live) {
  if (!live?.deliveredDate || live.businessDays == null) return null
  return overdueRefundWhy({ deliveredDate: live.deliveredDate, businessDays: live.businessDays, windowBusinessDays: step?.params?.overdueRefund?.windowBusinessDays })
}

// "RMA $X − return label $Y = $Z" (the label charge only when the customer
// pays for it), or the RMA amount alone.
export function overdueAmountBreakdown(step) {
  const o = step?.params?.overdueRefund ?? {}
  const money = (v) => `$${Number(v).toFixed(2)}`
  if (o.rmaAmount == null) return null
  if (o.labelCharge != null && Number(o.labelCharge) > 0 && o.amount != null) return `RMA ${o.rmaNumber ?? ''} ${money(o.rmaAmount)} − return label ${money(o.labelCharge)} = ${money(o.amount)}`.replace(/\s+/g, ' ')
  return `RMA ${o.rmaNumber ?? ''} ${money(o.rmaAmount)}`.replace(/\s+/g, ' ')
}

// The step states a reload restores: the refund's result and the email sent
// (CXHub notes on the suggestion, overdue-refund-process.js), and the note
// once its addOrderNote went through.
export function overdueRestoredStates(suggestion) {
  const calls = suggestion?.modelMeta?.apiCalls ?? []
  const states = {}
  ;(suggestion?.proposedActions ?? []).forEach((a, i) => {
    if (isOverdueRefundStep(a)) {
      const note = [...calls].reverse().find((c) => c?.overdueRefundResult && c.stepIndex === i && !c.error)
      if (note) states[i] = { status: 'success', error: null, result: { ...note.overdueRefundResult, erpReturnId: note.overdueRefundResult.rtnNumber ?? null, overdueAmount: note.overdueRefundResult.amount ?? null } }
    } else if (a?.type === OVERDUE_EMAIL_STEP) {
      if (calls.some((c) => c?.rmaZeroOutSent === true && c.stepIndex === i && !c.error)) states[i] = { status: 'success', error: null, result: { sent: true } }
    } else if (isOverdueNoteStep(a)) {
      if (calls.some((c) => c?.label === 'addOrderNote' && c.stepIndex === i && !c.error && c.phase === 'process')) states[i] = { status: 'success', error: null, result: null }
    }
  })
  return states
}
